/**
 * AppBridge - 与原生 App 通信的统一接口
 *
 * 这个模块提供了与 iOS 和 Android App 通信的统一接口，
 * 使 H5 可以轻松地调用原生功能，并处理原生回调。
 */

// 判断是否在浏览器环境
const isBrowser = typeof window !== 'undefined'

// 定义全局接口，以便 TypeScript 能够识别全局变量
declare global {
  interface Window {
    WebViewJavascriptBridge?: {
      callHandler: (handlerName: string, data?: any, responseCallback?: (responseData: any) => void) => void
      registerHandler: (
        handlerName: string,
        handler: (data: any, responseCallback: (responseData: any) => void) => void
      ) => void
    }
    hotCoin?: {
      [key: string]: any
      bridgeNavTitle?: (title: string) => void
      bridgeUrlRoute?: (url: string) => void
      bridgeTrade?: (type: string, id: string) => void
      bridgeNative?: () => void
      bridgeTelegramDeleteCookie?: () => void
      bridgeTelegramUserInfo?: (userInfo: string) => void
      bridgeVisaPayRecord?: () => void
      bridgeVisaPayParam?: () => string
    }
    monoBridge?: {
      analytics?: {
        recordH5Event?: {
          record: (event: string) => void
        }
      }
      responsiveFromUrl?: string[]
    }
  }
}

/**
 * 检查当前是否在 App 内运行
 * @returns 是否在 App 内
 */
export function isInApp(): boolean {
  if (!isBrowser) return false
  return !!window.WebViewJavascriptBridge || !!window.hotCoin
}

/**
 * 检查当前是否在iOS App内运行
 * @returns 是否在iOS App内
 */
export function isInIOS(): boolean {
  if (!isBrowser) return false
  return !!window.WebViewJavascriptBridge
}

/**
 * 检查当前是否在Android App内运行
 * @returns 是否在Android App内
 */
export function isInAndroid(): boolean {
  if (!isBrowser) return false
  return !!window.hotCoin && !window.WebViewJavascriptBridge
}

/**
 * 将数据序列化成字符串
 * @param data 需要序列化的数据
 * @returns 序列化后的字符串
 */
function serializeData(data: any): string {
  if (typeof data === 'string') {
    return data
  }
  try {
    return JSON.stringify(data)
  } catch (e) {
    console.error('序列化数据失败:', e)
    return ''
  }
}

/**
 * 解析数据
 * @param data 需要解析的字符串数据
 * @returns 解析后的对象
 */
function parseData<T>(data: string): T {
  if (!data) return {} as T

  try {
    return JSON.parse(data) as T
  } catch (e) {
    console.error('解析数据失败:', e)
    return {} as T
  }
}

/**
 * App通信接口类
 */
class AppBridge {
  /**
   * 设置导航栏标题
   * @param title 标题文本
   */
  setNavTitle(title: string): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeNavTitle', title)
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeNavTitle?.(title)
      }
    } catch (e) {
      console.error('设置导航栏标题失败:', e)
    }
  }

  /**
   * 通过URL路由跳转
   * @param options 路由参数，包含 path 和其他任意参数
   */
  navigateByUrl(options: { path: string; [key: string]: any }): void {
    if (!isBrowser || !isInApp()) return

    try {
      let url = `hotcoinH5://urlRoute?path=${options.path}`

      // 遍历除 path 以外的所有属性作为 URL 参数
      Object.entries(options).forEach(([key, value]) => {
        if (key !== 'path' && value !== undefined && value !== null) {
          url += `&${key}=${value}`
        }
      })

      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeUrlRoute', url)
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeUrlRoute?.(url)
      }
    } catch (e) {
      console.error('URL路由跳转失败:', e)
    }
  }

  /**
   * 回到主页
   */
  backToHome(): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeUrlRoute', 'hotcoinH5://urlRoute?router_open_cmd=3')
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeNative?.()
      }
    } catch (e) {
      console.error('回到主页失败:', e)
    }
  }

  /**
   * 跳转到交易页面
   * @param type 交易类型 ('contract' 或其他)
   * @param id 交易ID
   */
  navigateToTrade(type: string, id: string): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeTrade', `hotcoinH5://${type}/${id}`)
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeTrade?.(type, id)
      }
    } catch (e) {
      console.error('跳转到交易页面失败:', e)
    }
  }

  /**
   * 跳转到福利中心
   */
  navigateToWelfareCenter(): void {
    this.navigateByUrl({ path: 'welfare_center' })
  }

  // hotcoinH5://urlRoute?path=rechargeList&coinId=${this.applyResult.usdtCoinId}
  /**
   * 跳转到充币页面
   * @param coinId 币种ID
   */
  navigateToRecharge(coinId: string): void {
    this.navigateByUrl({ path: 'rechargeList', coinId })
  }

  // hotcoinH5://urlRoute?path=welfare_ticket&ctrlType=1
  /**
   * 跳转到福利券页面
   * @param type 福利券类型
   */
  navigateToWelfareTicket(type: string): void {
    this.navigateByUrl({ path: 'welfare_ticket', ctrlType: type })
  }

  // hotcoinH5://urlRoute?path=welfare_contractOpenTicketDetail&ticketId=${this.applyStatus.openTicketId}
  /**
   * 跳转到合约开通券详情页面
   * @param ticketId 券ID
   */
  navigateToContractOpenTicketDetail(ticketId: string): void {
    this.navigateByUrl({ path: 'welfare_contractOpenTicketDetail', ticketId })
  }

  /**
   * 跳转到永续合约页面
   */
  navigateToPerpetualContract(): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler(
          'bridgeUrlRoute',
          'hotcoinH5://urlRoute?path=mainPage&tab=contract_perpetual'
        )
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeUrlRoute?.('hotcoinH5://urlRoute?path=main_mainPage&tab=contract_perpetual')
      }
    } catch (e) {
      console.error('跳转到永续合约页面失败:', e)
    }
  }

  /**
   * 清除Telegram Cookie
   */
  clearTelegramCookie(): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeTelegramDeleteCookie')
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeTelegramDeleteCookie?.()
      }
    } catch (e) {
      console.error('清除Telegram Cookie失败:', e)
    }
  }

  /**
   * 发送Telegram用户信息到App
   * @param userInfo 用户信息对象
   */
  sendTelegramUserInfo(userInfo: any): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeTelegramUserInfo', userInfo)
      } else if (isInAndroid()) {
        const userInfoStr = serializeData(userInfo)
        window.hotCoin?.bridgeTelegramUserInfo?.(userInfoStr)
      }
    } catch (e) {
      console.error('发送Telegram用户信息失败:', e)
    }
  }

  /**
   * 记录H5事件
   * @param event 事件名称
   */
  recordEvent(event: string): void {
    try {
      if (window.monoBridge?.analytics?.recordH5Event?.record) {
        window.monoBridge.analytics.recordH5Event.record(event)
      }
    } catch (e) {
      console.error('记录H5事件失败:', e)
    }
  }

  /**
   * 通知App进行Visa支付记录
   */
  notifyVisaPayRecord(): void {
    if (!isBrowser || !isInApp()) return

    try {
      if (isInIOS()) {
        window.WebViewJavascriptBridge?.callHandler('bridgeVisaPayRecord')
      } else if (isInAndroid()) {
        window.hotCoin?.bridgeVisaPayRecord?.()
      }
    } catch (e) {
      console.error('通知App进行Visa支付记录失败:', e)
    }
  }

  /**
   * 从App获取Visa支付参数
   * @returns Promise解析为支付参数
   */
  getVisaPayParams<T>(): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!isBrowser || !isInApp()) {
        reject(new Error('不在App内'))
        return
      }

      try {
        if (isInIOS()) {
          window.WebViewJavascriptBridge?.registerHandler('bridgeVisaPayParam', (data) => {
            resolve(data as T)
          })
        } else if (isInAndroid()) {
          const paramStr = window.hotCoin?.bridgeVisaPayParam?.()
          if (paramStr) {
            const params = parseData<T>(paramStr)
            resolve(params)
          } else {
            reject(new Error('获取Visa支付参数失败'))
          }
        } else {
          reject(new Error('不支持的平台'))
        }
      } catch (e) {
        console.error('获取Visa支付参数失败:', e)
        reject(e)
      }
    })
  }

  /**
   * 注册一个通用的App回调处理函数（主要用于iOS）
   * @param handlerName 处理器名称
   * @param callback 回调函数
   */
  registerHandler<T = any, R = any>(handlerName: string, callback: (data: T) => R): void {
    if (!isInApp() || !isInIOS()) return

    try {
      window.WebViewJavascriptBridge?.registerHandler(handlerName, (data, responseCallback) => {
        const result = callback(data as T)
        if (responseCallback) {
          responseCallback(result)
        }
      })
    } catch (e) {
      console.error(`注册处理器 ${handlerName} 失败:`, e)
    }
  }

  /**
   * 获取 App Token
   * @returns Promise 解析为包含 token 的对象
   */
  getAppToken(): Promise<{ token: string }> {
    return new Promise((resolve, reject) => {
      if (!isBrowser || !isInApp()) {
        reject(new Error('不在App内'))
        return
      }

      try {
        if (isInApp()) {
          const bridge = window.hotCoin?.bridgeJsTokenHandler?.()
          if (bridge) {
            try {
              const parsedBridge = parseData<{ token: string }>(bridge)
              resolve({ token: parsedBridge.token })
            } catch (e) {
              reject(new Error('解析 token 失败'))
            }
          } else {
            reject(new Error('获取 token 失败'))
          }
        } else {
          reject(new Error('不支持的平台'))
        }
      } catch (e) {
        console.error('获取 App Token 失败:', e)
        reject(e)
      }
    })
  }

  /**
   * 调用App方法（通用方法）
   * @param handlerName 处理器名称
   * @param data 传递给APP的数据
   * @returns 返回Promise，解析为App的响应
   */
  callHandler<T = any, R = any>(handlerName: string, data?: T): Promise<R> {
    return new Promise((resolve, reject) => {
      if (!isBrowser || !isInApp()) {
        reject(new Error('不在App内'))
        return
      }

      try {
        if (isInIOS()) {
          window.WebViewJavascriptBridge?.callHandler(handlerName, data, (responseData) => {
            resolve(responseData as R)
          })
        } else if (isInAndroid() && window.hotCoin?.[handlerName]) {
          // Android需要特殊处理，因为没有统一的callHandler方法
          if (data !== undefined) {
            const serializedData = typeof data === 'string' ? data : serializeData(data)
            const result = window.hotCoin[handlerName](serializedData)
            // 尝试解析返回结果
            try {
              const parsedResult = result ? parseData<R>(result) : null
              resolve(parsedResult as R)
            } catch {
              resolve(result as R)
            }
          } else {
            const result = window.hotCoin[handlerName]()
            // 尝试解析返回结果
            try {
              const parsedResult = result ? parseData<R>(result) : null
              resolve(parsedResult as R)
            } catch {
              resolve(result as R)
            }
          }
        } else {
          reject(new Error('方法不可用'))
        }
      } catch (e) {
        console.error(`调用处理器 ${handlerName} 失败:`, e)
        reject(e)
      }
    })
  }
}

// 导出单例
export const appBridge = new AppBridge()
export default appBridge
